import { ChangeDetectionStrategy, Component } from '@angular/core';

/**
 * FieldsetComponent - Semantic HTML5 fieldset for form grouping
 *
 * Provides a styled fieldset element with legend support for organizing form fields.
 * Uses native HTML5 semantics for proper accessibility and form structure.
 *
 * @example Basic usage
 * ```html
 * <fieldset zagFieldset>
 *   <span slot="legend">Personal Details</span>
 *   <zag-form-field label="First Name">
 *     <zag-input formControlName="firstName" />
 *   </zag-form-field>
 *   <zag-form-field label="Last Name">
 *     <zag-input formControlName="lastName" />
 *   </zag-form-field>
 * </fieldset>
 * ```
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'fieldset[zagFieldset]',
  template: `
    <legend class="zag-fieldset__legend">
      <ng-content select="[slot=legend]" />
    </legend>
    <div class="zag-fieldset__content">
      <ng-content />
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-lg, 0.5rem);
        padding: var(--spacing-lg, 1.5rem);
        margin: 0;
        background: var(--surface-color-default);
      }

      .zag-fieldset__legend {
        font-size: var(--font-size-caption, 14px);
        font-weight: 600;
        color: var(--text-color-primary);
        padding: 0 var(--spacing-sm, 0.5rem);
      }

      .zag-fieldset__content {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-md, 1rem);
        width: 100%;
        flex-wrap: wrap;
        align-content: center;
        justify-content: center;
        align-items: center;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-fieldset',
  },
})
export class FieldsetComponent {}
